import {useCallback, useEffect, useRef} from 'react';
import {getPageDisplayIdentity} from '../../../../pages/shared';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  type SubJob,
} from '@tinct/web-components';
import {useGetCampaignById, useRegenerateCampaignOnError, useValidateCampaignAudience} from '@tinct/core-api';
import {useGetCurrentQuotas, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import type {EmbeddedPage} from '@tinct/core-api-models';
import {useSampleGenerationJobAlert} from './use-sample-generation-job-alert';

const POLL_INTERVAL_MS = 3000;

function pageToSubJob(page: EmbeddedPage, onRetry: () => void): SubJob {
  const company = page.lead?.company;
  const companyName = getPageDisplayIdentity(page).label;
  const companyInitial = companyName.charAt(0).toUpperCase();
  const logoUrl = company?.logo?.url;

  const jobStatus = page.lastGenerationJob?.status;
  const errorCode = page.lastGenerationJob?.errorCode;
  const statusMap: Record<string, SubJob['status']> = {
    completed: 'completed',
    running: 'ongoing',
    failed: 'error',
  };

  return {
    title: companyName,
    status: statusMap[jobStatus ?? ''] ?? 'queued',
    errorMessage: errorCode === 'not_enough_credits' ? 'Not enough credits' : undefined,
    icon: (
      <Avatar className="size-full rounded-md">
        {logoUrl && <AvatarImage src={logoUrl} alt={companyName} />}
        <AvatarFallback className="rounded-md text-xs">{companyInitial}</AvatarFallback>
      </Avatar>
    ),
    iconFill: true,
    progress: 0,
    strategy: 'linear',
    simulateProgress: jobStatus === 'running',
    simulationEstimatedTimeInSeconds: 30,
    simulationStartedAt: page.lastGenerationJob?.startedAt,
    onRetry: jobStatus === 'failed' ? onRetry : undefined,
  };
}

export function useSampleGenerationJob(campaignId: string) {
  const workspaceId = useGetCurrentWorkspaceId();
  const {mutateAsync: validateAudience} = useValidateCampaignAudience();
  const {mutateAsync: regenerateOnError} = useRegenerateCampaignOnError();
  const hasStartedRef = useRef(false);
  const {lpGenerationQuota} = useGetCurrentQuotas();

  const {data: campaign} = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        samplePages: {
          fields: {
            status: null,
            createdAt: null,
            updatedAt: null,
            lastGenerationJob: {
              fields: {
                id: null,
                startedAt: null,
                errorCode: null,
                errorMessage: null,
                name: null,
                status: null,
              }
            },
            lead: {
              fields: {
                company: {
                  fields: {
                    name: null,
                    logo: null,
                  },
                },
              },
            },
          },
        },
      },
    },
    {
      refetchInterval: (query) => {
        const pages = query.state.data?.samplePages ?? [];
        if (pages.length === 0) return POLL_INTERVAL_MS;
        const allTerminal = pages.every((p) => {
          const s = p.lastGenerationJob?.status;
          return s === 'completed' || s === 'failed';
        });
        return allTerminal ? false : POLL_INTERVAL_MS;
      },
    }
  );

  const samplePages = campaign?.samplePages ?? [];
  const allReady = samplePages.length > 0 && samplePages.every((p) => p.lastGenerationJob?.status === 'completed');
  const readyCount = samplePages.filter((p) => p.lastGenerationJob?.status === 'completed').length;
  const allError = samplePages.length > 0 && samplePages.every((p) => p.lastGenerationJob?.status === 'failed');
  const hasNotEnoughCredits = samplePages.some((p) => p.lastGenerationJob?.errorCode === 'not_enough_credits');
  const needToStart = samplePages.every((p) => p.lastGenerationJob === null) && samplePages.length > 0;

  // Pick a representative generation job to register the alert under — the first running page,
  // or the first completed one once everything's terminal.
  const representativeJobId =
    samplePages.find((p) => p.lastGenerationJob?.status === 'running')?.lastGenerationJob?.id ??
    samplePages.find((p) => p.lastGenerationJob?.id)?.lastGenerationJob?.id;

  const {start: startJobAlert, gameModal} = useSampleGenerationJobAlert(campaignId);

  useEffect(() => {
    if (representativeJobId) {
      startJobAlert(representativeJobId);
    }
  }, [representativeJobId, startJobAlert]);

  // Proactive quota check
  const remainingCredits = lpGenerationQuota?.remainingQuota ?? 0;
  const pagesNeedingGeneration = needToStart
    ? samplePages.length
    : samplePages.filter((p) => p.status === 'queued' || p.status === 'error').length;
  const quotaInsufficient = pagesNeedingGeneration > 0 && remainingCredits < pagesNeedingGeneration;

  // Start generation once (new run only)
  useEffect(() => {
    if (needToStart && !hasStartedRef.current) {
      hasStartedRef.current = true;
      if (quotaInsufficient) return;
      void validateAudience({id: campaignId, organizationId: workspaceId});
    }
  }, [needToStart, validateAudience, campaignId, quotaInsufficient, workspaceId]);

  const retryAll = useCallback(() => {
    if (quotaInsufficient) return;
    void validateAudience({id: campaignId, organizationId: workspaceId});
  }, [validateAudience, campaignId, quotaInsufficient, workspaceId]);

  // Per-page retry: regenerate every errored page via the campaign-level
  // _regenerate-on-error endpoint (vs. retryAll, which re-validates the whole
  // audience).
  const retryErroredPages = useCallback(() => {
    if (quotaInsufficient) return;
    void regenerateOnError({id: campaignId, organizationId: workspaceId});
  }, [regenerateOnError, campaignId, quotaInsufficient, workspaceId]);

  const remainingLabel = allError
    ? 'Generation failed. Please try again.'
    : allReady
      ? 'All pages ready!'
      : `${readyCount} of ${samplePages.length} pages ready`;

  const subJobs: SubJob[] = samplePages.map((page) => pageToSubJob(page, retryErroredPages));

  // Surface a top-level failure only when every page failed AND it's not the
  // credit-paywall path (which is handled separately).
  const isGenerationFailed = allError && !hasNotEnoughCredits && !quotaInsufficient;

  return {
    subJobs,
    allReady,
    isGenerationFailed,
    hasNotEnoughCredits: hasNotEnoughCredits || quotaInsufficient,
    remainingLabel,
    pageCount: samplePages.length,
    retryAll,
    gameModal,
  };
}
